/**
 * Stacked formations — finding them on screen, and fanning them out.
 *
 * Owner, 2026-09-11: *"När många enheter samlas när så är det svårt att välja.
 * Skapa en presentera enheter i hög i en ring när man hovrar över dom."*
 *
 * Pure functions on screen coordinates: the map projects, this decides. Kept
 * out of App.tsx so the geometry is tested without a browser.
 */

export interface ScreenPoint {
  id: number;
  x: number;
  y: number;
}

export interface Offset {
  dx: number;
  dy: number;
}

/**
 * Groups of formations drawn close enough to cover each other.
 *
 * Single-link: if A overlaps B and B overlaps C, all three are one heap even
 * when A and C do not touch — two rings sharing a member would open on top of
 * each other, which is the problem being solved. Only groups of two or more.
 */
export function findStacks(points: ScreenPoint[], radiusPx: number): number[][] {
  const parent = points.map((_, i) => i);
  const root = (i: number): number => (parent[i] === i ? i : (parent[i] = root(parent[i])));
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      if (Math.hypot(points[i].x - points[j].x, points[i].y - points[j].y) <= radiusPx) {
        parent[root(i)] = root(j);
      }
    }
  }
  const groups = new Map<number, number[]>();
  points.forEach((p, i) => {
    const r = root(i);
    groups.set(r, [...(groups.get(r) ?? []), p.id]);
  });
  return [...groups.values()].filter((g) => g.length > 1);
}

/**
 * Places on a ring for `n` formations, relative to the heap's centre.
 *
 * The radius is the smallest that keeps neighbours `gapPx` apart — the chord
 * between them is 2r·sin(π/n) — and never less than the gap itself, so a pair
 * does not open right under the pointer. The first place is straight up and the
 * rest follow clockwise: the same heap always opens the same way.
 */
export function ringOffsets(n: number, gapPx: number): Offset[] {
  if (n <= 0) return [];
  if (n === 1) return [{ dx: 0, dy: 0 }];
  const r = Math.max(gapPx / (2 * Math.sin(Math.PI / n)), gapPx);
  return Array.from({ length: n }, (_, i) => {
    const a = -Math.PI / 2 + (2 * Math.PI * i) / n;
    return { dx: r * Math.cos(a), dy: r * Math.sin(a) };
  });
}
